import { Stack, Text, Center } from '@mantine/core';
import { Database } from 'lucide-react';
import { DataLibraryToolbar } from './DataLibraryToolbar';
import { DataLibraryTable } from './DataLibraryTable';
import { useDataLibraryParticipants, type ParticipantsTabConfig } from './useDataLibraryParticipants';

interface DataLibraryParticipantsTabProps {
  selectedCohortIds: string[];
  initialConfig?: ParticipantsTabConfig;
}

export function DataLibraryParticipantsTab({
  selectedCohortIds,
  initialConfig,
}: DataLibraryParticipantsTabProps) {
  const {
    participants,
    groupedParticipants,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize,
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeQuickFilters,
    handleQuickFilterToggle,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    activeGroup,
    setActiveGroup,
    columns,
    setColumns,
    visibleColumns,
    groupLimitTotal,
  } = useDataLibraryParticipants({ selectedCohortIds, initialConfig });

  if (selectedCohortIds.length === 0) {
    return (
      <Center h={300}>
        <Stack align="center" gap="sm">
          <Database size={40} color="#888" />
          <Text c="dimmed" size="lg">
            Select one or more cohorts to view participants
          </Text>
          <Text c="dimmed" size="sm">
            Use the side panel to choose cohorts
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="sm">
      <DataLibraryToolbar
        groupLimitTotal={groupLimitTotal}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        filterOptions={filterOptions}
        activeQuickFilters={activeQuickFilters}
        onQuickFilterToggle={handleQuickFilterToggle}
        activeFilters={activeFilters}
        onFilterAdd={handleFilterAdd}
        onFilterChange={handleFilterChange}
        onFilterRemove={handleFilterRemove}
        onClearAll={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
        sortConfig={sortConfig}
        onSortChange={handleSortPanelChange}
        activeGroup={activeGroup}
        onGroupChange={setActiveGroup}
        columns={columns}
        onColumnsChange={setColumns}
        totalCount={totalCount}
        selectedCohortIds={selectedCohortIds}
      />

      <DataLibraryTable
        data={participants}
        averages={averages}
        groupedData={groupedParticipants}
        columns={visibleColumns}
        sortConfig={sortConfig}
        onSort={handleSort}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearAllFilters}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </Stack>
  );
}
